// A bench's boards in its panel: the slat / gap / thickness table, the skirting,
// a steam bench's profile preset. A patch from them is written through the
// bench's adapter (roomBench.ts). docs/claude/benches.md.
import {
  cloneSkirting,
  DEFAULT_SKIRTING,
} from '../../shared/benchPresets';
import {
  DEFAULT_STEAM_BENCH_PRESETS,
  DEFAULT_STEAM_PRESET_ID,
} from '../../shared/steamBenchPresets';
import {
  type BenchSlatPreset,
  type ProductInstance,
  type SkirtingSpec,
  type SteamBenchPreset,
} from '../../shared/types';
import {
  normalizeProfile,
  profileExtent,
  profileSeatHeight,
} from '../benchProfile';
import { SKIRTING_MIN_CUT } from '../benchSolids';
import {
  benchOverlap,
  MIN_BENCH_DEPTH,
} from '../benches';
import { showAlert } from '../dialog';
import { DEFAULT_SLAT_THICKNESS } from '../heights';
import { tr } from '../i18n';
import {
  buttonRow,
  infoRow,
  numberRow,
  parseMeasure,
  sectionTitle,
  selectRow,
  textFieldRow,
} from './panelRows';
import { platform } from '../platform';
import { polyBenchDepth } from '../polyRoom';
import {
  type PolyBench,
  type ProductBenchLeg,
  type SlatPatch,
} from '../roomBench';
import {
  benchPresets,
  benchPresetsSorted,
  steamPresets,
} from './standards';
import {
  canEditStandards,
  editRoom,
} from '../main';

/** How one board table reads: its value column's header, its Add / Delete
 *  labels, the text shown with no board, the value / gap / thickness a first
 *  board takes, and whether its last board can be deleted. */
interface BoardTableText { value: string; add: string; del: string; empty: string; first: [number, number, number]; keepOne?: boolean }

/** A board table — a bench's slats or its skirting: a value / Gap / Thickness
 *  row per board (header on top) with a delete button, and an Add button that
 *  repeats the last row. `commit(values, gaps, thick)` gets the whole list after
 *  each edit. */
function buildBoardTable(body: HTMLElement, rows: { values: number[]; gaps: number[]; thick: number[] }, text: BoardTableText, commit: (values: number[], gaps: number[], thick: number[]) => void): void {
  const values = rows.values.slice(), gaps = rows.gaps.slice(), thick = rows.thick.slice();
  const [firstV, firstG, firstT] = text.first;
  // A commit rebuilds the table, so Tab is the table's own: it commits the cell,
  // then focuses the next one (row by row) in the table as rebuilt.
  const cellKey = (i: number, c: number): string => `${text.value}:${i}:${c}`;
  const cellInput = (value: number, i: number, c: number, onCommit: (v: number) => void): HTMLInputElement => {
    const inp = document.createElement('input');
    inp.type = 'text'; inp.inputMode = 'decimal';
    inp.dataset.enterCommits = '';
    inp.dataset.boardCell = cellKey(i, c);
    inp.value = String(Math.round(value));
    inp.addEventListener('change', () => { const v = parseMeasure(inp.value, 'mm'); if (v !== null && Number.isFinite(v)) onCommit(v); });
    inp.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { inp.blur(); return; }
      if (e.key !== 'Tab') return;
      const k = i * 3 + c + (e.shiftKey ? -1 : 1);
      if (k < 0 || k >= values.length * 3) return; // out of the table: the browser's own Tab
      e.preventDefault();
      inp.blur();
      const next = document.querySelector<HTMLInputElement>(`[data-board-cell="${cellKey(Math.floor(k / 3), k % 3)}"]`);
      next?.focus(); next?.select();
    });
    return inp;
  };
  if (values.length === 0) {
    const empty = document.createElement('div'); empty.className = 'prop-empty';
    empty.textContent = tr(text.empty);
    body.appendChild(empty);
  } else {
    const table = document.createElement('table');
    table.className = 'slat-table';
    const thead = document.createElement('thead');
    const htr = document.createElement('tr');
    for (const h of ['#', text.value, 'Gap', 'Thickness', '']) {
      const th = document.createElement('th'); th.textContent = h && h !== '#' ? tr(h) : h; htr.appendChild(th);
    }
    thead.appendChild(htr); table.appendChild(thead);
    const tbody = document.createElement('tbody');
    values.forEach((w, i) => {
      const row = document.createElement('tr');
      const cNum = document.createElement('td'); cNum.className = 'slat-idx'; cNum.textContent = String(i + 1);
      const wIn = cellInput(w, i, 0, (v) => { const nv = values.slice(); nv[i] = Math.max(1, v); commit(nv, gaps.slice(), thick.slice()); });
      const gIn = cellInput(gaps[i] ?? 0, i, 1, (v) => { const ng = gaps.slice(); while (ng.length < values.length) ng.push(0); ng[i] = Math.max(0, v); commit(values.slice(), ng, thick.slice()); });
      const tIn = cellInput(thick[i] ?? firstT, i, 2, (v) => { const nt = thick.slice(); while (nt.length < values.length) nt.push(firstT); nt[i] = Math.max(0, v); commit(values.slice(), gaps.slice(), nt); });
      const cW = document.createElement('td'); cW.appendChild(wIn);
      const cG = document.createElement('td'); cG.appendChild(gIn);
      const cT = document.createElement('td'); cT.appendChild(tIn);
      const cD = document.createElement('td');
      if (!text.keepOne || values.length > 1) {
        const del = document.createElement('button');
        del.className = 'mini'; del.textContent = '✕'; del.title = tr(text.del);
        del.addEventListener('click', () => { const nv = values.slice(); nv.splice(i, 1); const ng = gaps.slice(); ng.splice(i, 1); const nt = thick.slice(); nt.splice(i, 1); commit(nv, ng, nt); });
        cD.appendChild(del);
      }
      row.append(cNum, cW, cG, cT, cD);
      tbody.appendChild(row);
    });
    table.appendChild(tbody);
    body.appendChild(table);
  }
  body.appendChild(buttonRow(text.add, () => {
    const lastW = values[values.length - 1] ?? firstV;
    const lastG = gaps[gaps.length - 1] ?? firstG;
    const lastT = thick[thick.length - 1] ?? firstT;
    const nextGaps = gaps.slice(0, values.length); while (nextGaps.length < values.length) nextGaps.push(lastG);
    commit([...values, lastW], [...nextGaps, lastG], [...thick.slice(0, values.length), lastT]);
  }));
}

/** The slat table (`buildBoardTable`: Width / Gap / Thickness, each gap on the
 *  slat's wall side) and the derived, read-only bench slab thickness (the
 *  thickest seat slat: every board but the front board, the last). Shared by the properties-panel bench editor and the Bench
 *  Presets editor. `apply(patch)` performs the host mutation. */
export function buildSlatTable(body: HTMLElement, host: SlatHost, apply: (patch: SlatPatch) => void): void {
  const slats = host.slats ?? [];
  const thick = host.slatThickness ?? [];
  buildBoardTable(body, { values: slats, gaps: host.gaps ?? [], thick }, {
    value: 'Width', add: '＋ Add slat', del: 'Delete slat', empty: 'No slats yet — add one below or pick a preset above.', first: [90, 10, DEFAULT_SLAT_THICKNESS],
  }, (nextSlats, nextGaps, nextThick) => {
    const st = nextThick.slice(0, nextSlats.length);
    while (st.length < nextSlats.length) st.push(nextThick[nextThick.length - 1] ?? DEFAULT_SLAT_THICKNESS);
    apply({
      slats: nextSlats.length ? nextSlats : undefined,
      gaps: nextGaps,
      slatThickness: nextSlats.length ? st.map((t) => Math.max(0, Math.round(t))) : undefined,
      slatPresetId: undefined,
    });
  });
  if (slats.length) {
    // The frame stands under the seat slats: every board but the front board (the last).
    const seat = Math.max(0, ...thick.slice(0, slats.length >= 2 ? slats.length - 1 : slats.length).filter((t) => t > 0));
    body.appendChild(infoRow('Bench thickness (mm)', `${Math.round(seat > 0 ? seat : DEFAULT_SLAT_THICKNESS)} (${tr('thickest seat slat')})`));
  }
}

/** The bench's skirting: No skirting / With skirting and, while it is on, its
 *  boards top first — a Height / Gap / Thickness table (each gap the one above
 *  its board, the first one under the front board) and the Clearance left above
 *  the floor or the seat below. The list repeats down; the lowest board is cut to
 *  the clearance (`skirtingLayout`). Switched on with no boards it starts as
 *  DEFAULT_SKIRTING; switched off its boards are kept. A board edit carries no
 *  `skirting` key, which is how the bench panel tells it from the switch. Shared
 *  by the bench panel and the preset editor. */
export function buildSkirtingSection(body: HTMLElement, host: SlatHost, apply: (patch: SlatPatch) => void): void {
  body.appendChild(selectRow('Skirting', [['none', 'No skirting'], ['skirting', 'With skirting']], host.skirting ? 'skirting' : 'none', (v) => {
    const on = v === 'skirting';
    apply(on && !host.skirtingBoards ? { skirting: true, skirtingBoards: cloneSkirting(DEFAULT_SKIRTING) } : { skirting: on });
  }));
  if (!host.skirting) return;
  const spec = host.skirtingBoards ?? DEFAULT_SKIRTING;
  const [b0, g0, t0] = [DEFAULT_SKIRTING.boards[0], DEFAULT_SKIRTING.gaps[0], DEFAULT_SKIRTING.thickness[0]];
  buildBoardTable(body, { values: spec.boards, gaps: spec.gaps, thick: spec.thickness }, {
    value: 'Height', add: '＋ Add board', del: 'Delete board', empty: 'No boards yet — add one below.', first: [b0, g0, t0], keepOne: true,
  }, (boards, gaps, thick) => {
    const g = gaps.slice(0, boards.length); while (g.length < boards.length) g.push(g0);
    const t = thick.slice(0, boards.length); while (t.length < boards.length) t.push(t[t.length - 1] ?? t0);
    apply({ skirtingBoards: { boards: boards.map((b) => Math.max(SKIRTING_MIN_CUT, Math.round(b))), gaps: g.map((x) => Math.max(0, Math.round(x))), thickness: t.map((x) => Math.max(1, Math.round(x))), clearance: spec.clearance } });
  });
  body.appendChild(numberRow('Clearance (mm)', Math.round(spec.clearance), (v) => {
    apply({ skirtingBoards: { ...cloneSkirting(spec), clearance: Math.max(0, Math.round(v)) } });
  }, { commit: true }));
}

/** A bench panel's skirting card: `buildSkirtingSection`, where a board edit
 *  makes the bench Custom (it no longer matches its preset) and the switch does
 *  not; a room's bench also sets its skirting back behind its front board (an
 *  A1 strip's room, `setBack`). */
export function appendSkirtingCard(body: HTMLElement, host: SlatHost, apply: (patch: SlatPatch) => void, setBack = false): void {
  body.appendChild(sectionTitle('Skirting'));
  buildSkirtingSection(body, host, (patch) => apply('skirting' in patch || 'skirtingSetBack' in patch ? patch : { ...patch, slatPresetId: undefined }));
  if (setBack && host.skirting) {
    body.appendChild(numberRow('Skirting set back (mm)', host.skirtingSetBack ?? 0, (v) => apply({ skirtingSetBack: Math.max(0, Math.round(v)) }), { commit: true }));
  }
}

/** A bench that can carry a steam profile: a wall bench or a partition's. */
type ProfileHost = PolyBench | ProductBenchLeg;

/** Give a polygonal bench a steam preset's profile (copied, so the drawing stands
 *  alone): its depth becomes the profile's and its height its seat; it carries
 *  no slats. `null` takes the profile off, back to a plain block. */
export function applyBenchProfile(pb: ProfileHost, preset: SteamBenchPreset | null): void {
  if (!preset) {
    pb.profile = undefined; pb.profilePresetId = undefined; pb.height = undefined;
    return;
  }
  const profile = normalizeProfile(preset.profile);
  pb.profile = profile;
  pb.profilePresetId = preset.id;
  pb.depth = Math.max(MIN_BENCH_DEPTH, profileExtent(profile).width);
  pb.height = Math.round(profileSeatHeight(profile));
  pb.slatThickness = undefined;
  const slatted = pb as PolyBench; // a partition bench has none of these; clearing them adds nothing a save keeps
  slatted.slats = undefined; slatted.gaps = undefined; slatted.slatPresetId = undefined; slatted.overlap = undefined;
}

/** A new wall bench on `edge` (over `span`, else the whole wall): a steam room's
 *  starts as the default steam preset (Steam A), a sauna's as a plain block of
 *  the room's default depth. */
export function newPolyBench(inst: ProductInstance, id: string, edge: number, span?: [number, number] | null): PolyBench {
  const pb: PolyBench = { id, edge, depth: polyBenchDepth(inst), ...(span ? { start: span[0], end: span[1] } : {}) };
  const preset = inst.category === 'steamroom' ? defaultSteamPreset() : null;
  if (preset) applyBenchProfile(pb, preset);
  return pb;
}

/** The preset a new steam-room bench starts with (Steam A). */
export function defaultSteamPreset(): SteamBenchPreset | null {
  return steamPresets.find((p) => p.id === DEFAULT_STEAM_PRESET_ID)
    ?? DEFAULT_STEAM_BENCH_PRESETS.find((p) => p.id === DEFAULT_STEAM_PRESET_ID) ?? null;
}

/** The steam presets sorted by name (numeric-aware), as every list shows them. */
export function steamPresetsSorted(): SteamBenchPreset[] {
  return [...steamPresets].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
}

/** A steam bench's [Profile] card: the preset it is built from (or a plain block). */
export function buildProfileSection(body: HTMLElement, room: ProductInstance, pb: ProfileHost): void {
  body.appendChild(sectionTitle('Profile'));
  const row = document.createElement('div');
  row.className = 'prop-row';
  const lab = document.createElement('label');
  lab.textContent = tr('Preset');
  const sel = document.createElement('select');
  const none = document.createElement('option');
  none.value = '';
  none.textContent = pb.profile ? tr('— Custom —') : tr('— None (block) —');
  sel.appendChild(none);
  for (const p of steamPresetsSorted()) {
    const opt = document.createElement('option');
    opt.value = p.id;
    opt.textContent = p.name;
    sel.appendChild(opt);
  }
  sel.value = pb.profile && pb.profilePresetId && steamPresets.some((p) => p.id === pb.profilePresetId) ? pb.profilePresetId : '';
  sel.addEventListener('change', () => {
    const preset = steamPresets.find((p) => p.id === sel.value) ?? null;
    if (!preset && !pb.profile) return;
    editRoom(room, () => applyBenchProfile(pb, preset));
  });
  row.append(lab, sel);
  body.appendChild(row);
  if (pb.profile) body.appendChild(infoRow('Overall height (mm)', String(Math.round(profileExtent(pb.profile).height))));
}

/** The slat-carrying fields common to rectangular bench legs and polygonal benches. */
interface SlatHost { slats?: number[]; gaps?: number[]; slatThickness?: number[]; overlap?: number; slatPresetId?: string; skirting?: boolean; skirtingBoards?: SkirtingSpec; skirtingSetBack?: number }

/**
 * The unified sauna-bench slat (doghe) editor — shared by rectangular and
 * polygonal saunas: recall a named preset, edit slat widths / gaps as per-row
 * entries, add / remove rows, set the overlap (disabled for the wall-touching
 * bench), and save the current composition as a preset. `apply(patch)` performs
 * the host-specific mutation (+ history, regen, repaint).
 */
export function buildSlatSection(body: HTMLElement, host: SlatHost, index: number, apply: (patch: SlatPatch) => void, upper?: { depth: number; slats?: number[]; gaps?: number[] } | null): void {
  const isWall = index <= 0;
  body.appendChild(sectionTitle('Slats (doghe)'));

  // Preset recall dropdown.
  const pRow = document.createElement('div');
  pRow.className = 'prop-row';
  const pLab = document.createElement('label');
  pLab.textContent = tr('Preset');
  const pSel = document.createElement('select');
  const none = document.createElement('option');
  none.value = '';
  none.textContent = tr(host.slats && host.slats.length ? '— Custom —' : '— None —');
  pSel.appendChild(none);
  const choices = benchPresetsSorted();
  for (const preset of choices) {
    const opt = document.createElement('option');
    opt.value = preset.id;
    opt.textContent = preset.name;
    pSel.appendChild(opt);
  }
  pSel.value = host.slatPresetId && choices.some((p) => p.id === host.slatPresetId) ? host.slatPresetId : '';
  pSel.addEventListener('change', () => {
    const preset = choices.find((p) => p.id === pSel.value);
    if (!preset) return;
    const st = preset.slatThickness && preset.slatThickness.length ? [...preset.slatThickness] : preset.slats.map(() => DEFAULT_SLAT_THICKNESS);
    apply({
      slats: [...preset.slats], gaps: [...(preset.gaps ?? [])], slatThickness: st, overlap: preset.overlap, slatPresetId: preset.id,
      skirting: !!preset.skirting, skirtingBoards: preset.skirtingBoards ? cloneSkirting(preset.skirtingBoards) : undefined,
    });
  });
  pRow.append(pLab, pSel);
  body.appendChild(pRow);

  buildSlatTable(body, host, apply);

  // Overlap: the wall-touching bench has nothing above it, so its overlap is 0.
  if (isWall) {
    body.appendChild(infoRow('Overlap (mm)', `0 (${tr('wall bench')})`));
  } else {
    body.appendChild(numberRow('Overlap (mm)', Math.round(benchOverlap(host, index, upper)), (v) => {
      apply({ overlap: Math.max(0, Math.round(v)) });
    }, { commit: true }));
  }

  // Save the current composition as a named preset (Baserow-backed). Gated on the
  // `edit` permission — a read-only user can pick presets but not save new ones.
  if (host.slats && host.slats.length && canEditStandards()) {
    let presetName = host.slatPresetId ? (benchPresets.find((p) => p.id === host.slatPresetId)?.name ?? '') : '';
    body.appendChild(textFieldRow('New preset name', presetName, 'e.g. Comfort 3×120', (v) => { presetName = v; }));
    body.appendChild(buttonRow('Save as preset', async () => {
      const name = presetName.trim();
      if (!name) { void showAlert(tr('Enter a preset name first.')); return; }
      const id = 'preset-' + name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      const preset: BenchSlatPreset = {
        rowId: benchPresets.find((p) => p.id === id)?.rowId,
        id, name,
        slats: [...(host.slats ?? [])],
        gaps: [...(host.gaps ?? [])],
        slatThickness: [...(host.slatThickness ?? [])],
        ...(host.overlap != null ? { overlap: host.overlap } : {}),
        ...(host.skirting ? { skirting: true } : {}),
        ...(host.skirtingBoards ? { skirtingBoards: cloneSkirting(host.skirtingBoards) } : {}),
      };
      try {
        const saved = await platform.saveBenchPreset(preset);
        const i = benchPresets.findIndex((p) => p.id === saved.id);
        if (i >= 0) benchPresets[i] = saved; else benchPresets.push(saved);
        apply({ slatPresetId: saved.id });
      } catch {
        void showAlert(tr('Could not save the preset to the database.'));
      }
    }));
  }
}
